import { z } from 'zod';

// Platform-level roles, stored in shared D1 (roles + tenant_memberships tables).
// super_admin is cross-tenant; all others are scoped to one tenant via memberships.
export const ROLES = ['super_admin', 'owner', 'admin', 'manager', 'member', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export const RoleSchema = z.enum(ROLES);

// RBAC permission matrix. Add a permission here, then reference it in routes via
// requirePermission('tenders:create'). Keep this list flat; group by resource:action.
export const PERMISSIONS = [
  // tenders
  'tenders:read',
  'tenders:create',
  'tenders:update',
  'tenders:delete',
  // clients
  'clients:read',
  'clients:create',
  'clients:update',
  'clients:delete',
  // quotes
  'quotes:read',
  'quotes:create',
  'quotes:update',
  'quotes:delete',
  // takeoffs
  'takeoffs:read',
  'takeoffs:create',
  'takeoffs:update',
  'takeoffs:delete',
  // inventory
  'inventory:read',
  'inventory:write',
  // RFIs
  'rfis:read',
  'rfis:create',
  'rfis:update',
  // site visits
  'site_visits:read',
  'site_visits:create',
  'site_visits:update',
  // identity / tenant admin
  'users:read',
  'users:invite',
  'users:remove',
  'roles:assign',
  // platform (super_admin only)
  'platform:tenants',
  'platform:audit',
  'platform:billing',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PermissionSchema = z.enum(PERMISSIONS);

// Default role → permissions matrix. The auth package's RBAC evaluator falls
// back to this when a tenant hasn't overridden role_permissions in shared D1.
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  super_admin: PERMISSIONS, // all of them
  owner: [
    'tenders:read', 'tenders:create', 'tenders:update', 'tenders:delete',
    'clients:read', 'clients:create', 'clients:update', 'clients:delete',
    'quotes:read', 'quotes:create', 'quotes:update', 'quotes:delete',
    'takeoffs:read', 'takeoffs:create', 'takeoffs:update', 'takeoffs:delete',
    'inventory:read', 'inventory:write',
    'rfis:read', 'rfis:create', 'rfis:update',
    'site_visits:read', 'site_visits:create', 'site_visits:update',
    'users:read', 'users:invite', 'users:remove', 'roles:assign',
  ],
  admin: [
    'tenders:read', 'tenders:create', 'tenders:update', 'tenders:delete',
    'clients:read', 'clients:create', 'clients:update', 'clients:delete',
    'quotes:read', 'quotes:create', 'quotes:update', 'quotes:delete',
    'takeoffs:read', 'takeoffs:create', 'takeoffs:update', 'takeoffs:delete',
    'inventory:read', 'inventory:write',
    'rfis:read', 'rfis:create', 'rfis:update',
    'site_visits:read', 'site_visits:create', 'site_visits:update',
    'users:read', 'users:invite',
  ],
  manager: [
    'tenders:read', 'tenders:create', 'tenders:update',
    'clients:read', 'clients:create', 'clients:update',
    'quotes:read', 'quotes:create', 'quotes:update',
    'takeoffs:read', 'takeoffs:create', 'takeoffs:update',
    'inventory:read', 'inventory:write',
    'rfis:read', 'rfis:create', 'rfis:update',
    'site_visits:read', 'site_visits:create', 'site_visits:update',
    'users:read',
  ],
  member: [
    'tenders:read', 'tenders:update',
    'clients:read', 'clients:update',
    'quotes:read', 'quotes:update',
    'takeoffs:read', 'takeoffs:update',
    'inventory:read',
    'rfis:read', 'rfis:create',
    'site_visits:read', 'site_visits:create',
  ],
  viewer: [
    'tenders:read', 'clients:read', 'quotes:read', 'takeoffs:read',
    'inventory:read', 'rfis:read', 'site_visits:read',
  ],
};
